/**
 * Shared markdown parsing, chapter filtering, and chapter ordering
 * used by all 4 export formats.
 */

export interface MarkdownBlock {
  type: 'paragraph' | 'heading' | 'bullet' | 'numbered-list';
  level?: 1 | 2 | 3;
  text?: string;
  items?: string[];
}

export function parseMarkdownBlocks(raw: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = raw.split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') { i++; continue; }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({ type: 'heading', level: headingMatch[1].length as 1 | 2 | 3, text: headingMatch[2].trim() });
      i++; continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, '').trim());
        i++;
      }
      blocks.push({ type: 'bullet', items });
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, '').trim());
        i++;
      }
      blocks.push({ type: 'numbered-list', items });
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^#{1,3}\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
    }
  }

  return blocks;
}

export interface InlineSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

export function parseInlineFormatting(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  const regex = /(\*{1,3})((?:(?!\1).)+?)\1|([^*]+)/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match[3] !== undefined) {
      if (match[3]) segments.push({ text: match[3], bold: false, italic: false });
    } else {
      const stars = match[1].length;
      segments.push({ text: match[2], bold: stars >= 2, italic: stars === 1 || stars === 3 });
    }
  }

  if (segments.length === 0 && text) {
    segments.push({ text, bold: false, italic: false });
  }
  return segments;
}

export function stripInline(text: string): string {
  return text.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
}

interface ChapterLike {
  chapter_title: string;
  type: string;
  order_index: number;
  [key: string]: any;
}

export function filterChapters<T extends ChapterLike>(chapters: T[]): T[] {
  return chapters.filter(ch => {
    const t = ch.chapter_title.trim();
    return !/^(?:purpose|goal|objective|note|description|overview|summary|intent|aim)\s*[:*]/i.test(t);
  });
}

export function sortChapters<T extends ChapterLike>(chapters: T[]): T[] {
  return [...chapters].sort((a, b) => {
    if (a.type === 'conclusion' && b.type !== 'conclusion') return 1;
    if (b.type === 'conclusion' && a.type !== 'conclusion') return -1;
    if (a.type === 'introduction' && b.type !== 'introduction') return -1;
    if (b.type === 'introduction' && a.type !== 'introduction') return 1;
    return a.order_index - b.order_index;
  });
}

export function prepareChapters<T extends ChapterLike>(chapters: T[]): T[] {
  return sortChapters(filterChapters(chapters));
}
